import { useEffect, useRef, useState } from 'react';
import { ImagePlus, Trash2 } from 'lucide-react';

export const MAX_IMAGE_BYTES = 3 * 1024 * 1024;
const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];

interface ProductImageFieldProps {
  /** URL already stored on the product, if any. */
  currentUrl?: string | null;
  /** File chosen but not uploaded yet. */
  file: File | null;
  /** True once the operator asked to drop the stored image. */
  removed: boolean;
  onSelect: (file: File | null) => void;
  onRemove: () => void;
  onValidationError: (message: string) => void;
}

/**
 * Image picker for the catalog form.
 *
 * Nothing is sent while the form is open: the chosen file is staged and the
 * parent uploads it after the product itself is saved, because the upload
 * endpoint needs the product id. Removing a stored image is staged the same way,
 * so cancelling the dialog changes nothing on the server.
 */
export default function ProductImageField({
  currentUrl,
  file,
  removed,
  onSelect,
  onRemove,
  onValidationError,
}: ProductImageFieldProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!file) {
      setPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const shownUrl = previewUrl || (removed ? null : currentUrl || null);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selected = event.target.files?.[0] || null;
    if (!selected) {
      onSelect(null);
      return;
    }
    // A courtesy check only. The server decides from the actual bytes, because a
    // browser's declared type is just a claim.
    if (!ACCEPTED_TYPES.includes(selected.type)) {
      onValidationError('Подойдёт изображение JPEG, PNG, WebP или GIF');
      event.target.value = '';
      return;
    }
    if (selected.size > MAX_IMAGE_BYTES) {
      onValidationError('Файл больше 3 МБ. Выберите изображение меньшего размера.');
      event.target.value = '';
      return;
    }
    onSelect(selected);
  };

  const handlePick = () => inputRef.current?.click();

  const handleRemove = () => {
    if (inputRef.current) inputRef.current.value = '';
    onRemove();
  };

  return (
    <div className="f-group product-image-field">
      <label htmlFor="product-image-input">Изображение товара</label>
      <div className="product-image-row">
        <div className="product-image-preview">
          {shownUrl ? (
            <img src={shownUrl} alt="Изображение товара" />
          ) : (
            <span className="product-image-empty">Нет фото</span>
          )}
        </div>

        <div className="product-image-actions">
          <input
            id="product-image-input"
            ref={inputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            onChange={handleChange}
            className="sr-only"
          />
          <button type="button" className="btn btn-outline btn-sm" onClick={handlePick}>
            <ImagePlus size={14} /> {shownUrl ? 'Заменить' : 'Загрузить'}
          </button>
          {shownUrl && (
            <button type="button" className="btn btn-outline btn-sm product-image-remove" onClick={handleRemove}>
              <Trash2 size={14} /> Удалить
            </button>
          )}
          <span className="product-image-hint">JPEG, PNG, WebP или GIF, до 3 МБ</span>
          {file && <span className="product-image-staged">Будет загружено после сохранения: {file.name}</span>}
          {removed && !file && <span className="product-image-staged">Изображение будет удалено после сохранения</span>}
        </div>
      </div>
    </div>
  );
}
